import { useState } from "react";
import styled from "styled-components";
import { breakpoint, theme } from "@utilities";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { ClipLoaderCommon } from "@components";
import { authApi } from "@api";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { SuccessToast } from "src/Components/toasts/SuccessToast";
import { initializeAccountDetail, selectAccount, useAppDispatch, useAppSelector } from "@redux";
import { MIN_PASSWORD_LENGTH, signupOfferCopy, VERIFICATION_CODE_LENGTH } from "../signupOfferConfig";
import {
    clearRetainedPassword,
    closePasswordPrompt,
    markPasswordSet,
    usePasswordPrompt,
} from "../signupPasswordPrompt";

/**
 * Sets a real password on an account whose password was generated at sign-up.
 *
 * Takes whichever of two routes is open to it:
 *
 * - **Direct**, while the generated password is still held in memory. One screen,
 *   no email.
 * - **By code**, once it is not — after a reload, or on a later visit. The
 *   backend requires a current password for a direct change regardless of
 *   `is_password_randomly_generated`, so identity has to be proved some other
 *   way, and the reset endpoints take an emailed code instead.
 *
 * Rendered at app level so the header badge can raise it from any page.
 */
export const SignupPasswordModal = () => {
    const { isOpen, retained } = usePasswordPrompt();
    const { account } = useAppSelector(selectAccount);
    const dispatch = useAppDispatch();

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [otp, setOtp] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [codeSent, setCodeSent] = useState(false);
    const [isVerifyingCode, setIsVerifyingCode] = useState(false);
    /**
     * The code is checked on its own screen before the password is asked for, so
     * a wrong one is reported straight away rather than after the visitor has
     * chosen and confirmed a password. Same two-step order the Forgotten Password
     * page uses, which is also why the code is still sent again with the reset
     * below — that endpoint validates it in its own right.
     */
    const [isCodeVerified, setIsCodeVerified] = useState(false);

    if (!isOpen) return null;

    const email = retained?.email ?? account?.email ?? "";
    // The direct route needs the password the account was created with. Without
    // it there is nothing to send as `current_password`, so the code route is the
    // only one left.
    const canSetDirectly = Boolean(retained?.currentPassword);

    const close = () => {
        setPassword("");
        setConfirmPassword("");
        setOtp("");
        setCodeSent(false);
        setIsCodeVerified(false);
        closePasswordPrompt();
    };

    const finishedWithRealPassword = async () => {
        // Recorded first, before anything that can fail. The password is already
        // set by this point, so a refresh that errors must not cost us the record
        // and leave the prompt up for good.
        markPasswordSet(email);
        clearRetainedPassword();
        // The header badge keys off the account flag too, so it is re-read rather
        // than assumed — if the backend did not clear it, at least this device
        // knows better.
        const refreshed = await dispatch(initializeAccountDetail()).unwrap();
        // Logged because a badge that will not go away is otherwise indistinguishable
        // from a frontend caching bug: this says what the backend actually stored.
        console.info(
            "[SetPassword] is_password_randomly_generated after saving:",
            refreshed?.is_password_randomly_generated,
        );
        SuccessToast({ Message: "Password set" });
        close();
    };

    const validatePair = (): boolean => {
        if (password.length < MIN_PASSWORD_LENGTH) {
            ErrorToast({ Message: `Please use at least ${MIN_PASSWORD_LENGTH} characters` });
            return false;
        }
        if (password !== confirmPassword) {
            ErrorToast({ Message: "Those passwords do not match" });
            return false;
        }
        return true;
    };

    const handleSendCode = async () => {
        if (!email) {
            ErrorToast({ Message: "We don't have an email address for this account" });
            return;
        }
        setIsSendingCode(true);
        try {
            await authApi.forgotPassEmail({ email });
            setCodeSent(true);
        } catch (error: any) {
            ErrorToast({
                Message: error?.response?.data?.title || "We couldn't send that code",
            });
        } finally {
            setIsSendingCode(false);
        }
    };

    const handleVerifyCode = async () => {
        if (otp.length !== VERIFICATION_CODE_LENGTH) {
            ErrorToast({ Message: `Please enter the ${VERIFICATION_CODE_LENGTH}-digit code` });
            return;
        }
        setIsVerifyingCode(true);
        try {
            await authApi.verifyEmail({ otp, email });
            setIsCodeVerified(true);
        } catch (error: any) {
            ErrorToast({
                Message:
                    error?.response?.data?.data?.otp?.[0] ||
                    error?.response?.data?.title ||
                    "That code was not accepted",
            });
        } finally {
            setIsVerifyingCode(false);
        }
    };

    const handleSetDirectly = async () => {
        if (!validatePair()) return;
        setIsSaving(true);
        try {
            // `false` is the state to store: the password being set is the
            // visitor's own. Sending `true` re-flagged it as generated and kept
            // the prompt up.
            await authApi.changePass({
                new_password: password,
                current_password: retained!.currentPassword,
                is_password_randomly_generated: false,
            });
            await finishedWithRealPassword();
        } catch (error: any) {
            ErrorToast({
                Message: error?.response?.data?.title || "We couldn't save that password",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleSetWithCode = async () => {
        if (otp.length !== VERIFICATION_CODE_LENGTH) {
            ErrorToast({ Message: `Please enter the ${VERIFICATION_CODE_LENGTH}-digit code` });
            return;
        }
        if (!validatePair()) return;

        setIsSaving(true);
        try {
            // The code was already accepted on the previous screen; it goes with
            // the reset as well because that endpoint validates it in its own
            // right. Same pair the Forgotten Password page sends.
            await authApi.forgotPass({ new_password: password, otp, email });
            // The reset endpoint takes no flag, so it cannot record that the
            // password is now the visitor's own — without this the "Set password"
            // prompt would never go away. Safe to run as a no-op change because we
            // know the password we just set; best-effort because failing here
            // leaves a working password and only a stale prompt, which is far
            // better than reporting the reset itself as failed.
            try {
                await authApi.changePass({
                    new_password: password,
                    current_password: password,
                    is_password_randomly_generated: false,
                });
            } catch (flagError: any) {
                // Surfaced, not swallowed: this is the step that decides whether
                // the "Set password" prompt goes away, and hiding its failure is
                // what made a stuck badge look like a mystery.
                console.error(
                    "[SetPassword] clearing the generated-password flag failed:",
                    flagError?.response?.status,
                    flagError?.response?.data,
                );
            }
            await finishedWithRealPassword();
        } catch (error: any) {
            ErrorToast({
                Message:
                    error?.response?.data?.data?.otp?.[0] ||
                    error?.response?.data?.title ||
                    "We couldn't set that password",
            });
        } finally {
            setIsSaving(false);
        }
    };

    // Three screens, one after the other: ask to send the code, take the code,
    // then take the password. The direct route skips the first two entirely.
    const showCodeField = !canSetDirectly && codeSent && !isCodeVerified;
    const showPasswordFields = canSetDirectly || isCodeVerified;

    return (
        <>
            <Overlay onClick={() => !isSaving && close()} />
            <Dialog onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={close} aria-label="Close" disabled={isSaving}>
                    &times;
                </CloseButton>
                <BrandLogo src="/images/logos/Primary_logo_color.svg" alt="" />
                <Headline>{signupOfferCopy.passwordHeadline}</Headline>
                <Subtitle>
                    {showPasswordFields
                        ? signupOfferCopy.passwordSubtitle
                        : showCodeField
                          ? `Enter the ${VERIFICATION_CODE_LENGTH}-digit code we sent to ${email}.`
                          : `We'll email a ${VERIFICATION_CODE_LENGTH}-digit code to ${email} to confirm it's you.`}
                </Subtitle>

                {!canSetDirectly && !codeSent && (
                    <PrimaryButton onClick={handleSendCode} disabled={isSendingCode}>
                        {isSendingCode ? (
                            <ClipLoaderCommon color={theme.colors.button_text} size={22} margin={0} />
                        ) : (
                            "Email me a code"
                        )}
                    </PrimaryButton>
                )}

                {showCodeField && (
                    <>
                        <FieldWrapper>
                            <CodeInput
                                type="text"
                                inputMode="numeric"
                                pattern="\d*"
                                maxLength={VERIFICATION_CODE_LENGTH}
                                placeholder={"_".repeat(VERIFICATION_CODE_LENGTH)}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                disabled={isVerifyingCode}
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !isVerifyingCode) handleVerifyCode();
                                }}
                            />
                        </FieldWrapper>
                        <PrimaryButton onClick={handleVerifyCode} disabled={isVerifyingCode}>
                            {isVerifyingCode ? (
                                <ClipLoaderCommon
                                    color={theme.colors.button_text}
                                    size={22}
                                    margin={0}
                                />
                            ) : (
                                "Continue"
                            )}
                        </PrimaryButton>
                    </>
                )}

                {showPasswordFields && (
                    <>
                        <FieldWrapper>
                            <PasswordInput
                                type={showPassword ? "text" : "password"}
                                placeholder="Create a password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={isSaving}
                                autoFocus
                            />
                            <EyeIcon onClick={() => setShowPassword(!showPassword)}>
                                {showPassword ? <VisibilityOutlined /> : <VisibilityOffOutlined />}
                            </EyeIcon>
                        </FieldWrapper>
                        <Hint>{signupOfferCopy.passwordHint}</Hint>

                        <FieldWrapper>
                            <PasswordInput
                                type={showPassword ? "text" : "password"}
                                placeholder="Confirm password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={isSaving}
                                onKeyDown={(e) => {
                                    if (e.key !== "Enter" || isSaving) return;
                                    if (canSetDirectly) handleSetDirectly();
                                    else handleSetWithCode();
                                }}
                            />
                        </FieldWrapper>

                        <PrimaryButton
                            onClick={canSetDirectly ? handleSetDirectly : handleSetWithCode}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <ClipLoaderCommon
                                    color={theme.colors.button_text}
                                    size={22}
                                    margin={0}
                                />
                            ) : (
                                signupOfferCopy.passwordButton
                            )}
                        </PrimaryButton>
                    </>
                )}
            </Dialog>
        </>
    );
};

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    z-index: 1200;
`;

const Dialog = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 1201;
    width: 420px;
    max-width: calc(100vw - 32px);
    box-sizing: border-box;
    padding: 28px 24px 24px;
    border-radius: ${theme.borderRadius.large};
    background: ${theme.colors.white};
    display: flex;
    flex-direction: column;
    align-items: center;

    ${breakpoint.breakOnlyMobile`
        padding: 24px 16px 20px;
    `}
`;

const CloseButton = styled.button`
    position: absolute;
    top: 12px;
    right: 14px;
    border: none;
    background: none;
    font-size: 28px;
    line-height: 1;
    color: ${theme.colors.gray_3};
    cursor: pointer;
`;

const BrandLogo = styled.img`
    width: 176px;
    height: auto;
    margin-bottom: 8px;
`;

const Headline = styled.h2`
    margin: 0 0 6px;
    font-size: 24px;
    font-weight: 700;
    text-align: center;
    color: ${theme.colors.black};
`;

const Subtitle = styled.p`
    margin: 0 0 18px;
    font-size: 14px;
    text-align: center;
    color: ${theme.colors.gray_1};
`;

const FieldWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const PasswordInput = styled.input`
    width: 100%;
    height: 52px;
    padding: 0 50px 0 16px;
    box-sizing: border-box;
    border: 1px solid ${theme.colors.gray_5};
    border-radius: ${theme.borderRadius.medium};
    font-size: 16px;

    &:focus {
        outline: none;
        border-color: ${theme.colors.main};
    }
`;

const CodeInput = styled.input`
    width: 100%;
    height: 52px;
    /* Matches the gap the hint leaves under the password field below, so the code
       does not sit flush against it. */
    margin-bottom: 14px;
    padding: 0 16px;
    box-sizing: border-box;
    border: 1px solid ${theme.colors.gray_5};
    border-radius: ${theme.borderRadius.medium};
    font-size: 20px;
    letter-spacing: 0.4em;
    text-align: center;

    &:focus {
        outline: none;
        border-color: ${theme.colors.main};
    }
`;

const EyeIcon = styled.span`
    position: absolute;
    top: 50%;
    right: 14px;
    transform: translateY(-50%);
    display: flex;
    color: ${theme.colors.gray_3};
    cursor: pointer;
`;

const Hint = styled.p`
    width: 100%;
    margin: 6px 0 14px;
    font-size: 13px;
    color: ${theme.colors.gray_3};
`;

const PrimaryButton = styled.button`
    width: 100%;
    height: 48px;
    margin-top: 14px;
    border: none;
    border-radius: ${theme.borderRadius.small};
    background: ${theme.colors.main};
    color: ${theme.colors.button_text};
    font-size: 17px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;
